import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { primaryColor, secondaryColor } from '../../../../utils/comon';
import CustomMultiSelect from '../../../common/formfields/CustomMultiSelect';
import CustomSelect from '../../../common/formfields/CustomSelect';
import apiServices from '../../../../services/apiService';
import { uploadImageToAzure } from '../../../../services/AzureStorageService';

const maintenanceCategories = [
    { label: "CAM (Common Area Maintenance)", value: "CAM (Common Area Maintenance)" },
    { label: "HVAC Servicing", value: "HVAC Servicing" },
    { label: "Security Services", value: "Security Services" },
    { label: "Water Supply & Plumbing", value: "Water Supply & Plumbing" },
    { label: "Waste Management", value: "Waste Management" },
    { label: "Electricity & Power Backup", value: "Electricity & Power Backup" },
    { label: "Elevator Maintenance", value: "Elevator Maintenance" },
    { label: "Monthly Maintenance", value: "Monthly Maintenance" },
    { label: "Other", value: "Other" }
];

const SplitEqualityForm = ({ units = [], siteAdminData }) => {
    const navigate = useNavigate();
    const siteId = useSelector((state) => state.auth.siteId);

    const [isLoading, setIsLoading] = useState(false);
    const [qrFile, setQrFile] = useState(null);

    const [formData, setFormData] = useState({
        dueDate: '',
        penaltyAmount: '0',
        cgstPercentage: '9',
        sgstPercentage: '9',
        selectedUnits: []
    });

    const [lineItems, setLineItems] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const handleCategorySelect = (selected) => {
        setSelectedCategories(selected);
        const selectedValues = selected.map(s => s.value);
        
        setLineItems(prevItems => {
            const newItems = prevItems.filter(item => selectedValues.includes(item.description));
            selectedValues.forEach(val => {
                if (!newItems.find(item => item.description === val)) {
                    newItems.push({
                        id: val + '-' + Date.now(),
                        description: val,
                        customDescription: "",
                        sacCode: "999598",
                        amount: "",
                        isGstApplicable: true
                    });
                }
            });
            return newItems;
        });
    };

    const handleLineItemChange = (id, field, value) => {
        setLineItems(prev => prev.map(item => 
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const calculateTotals = () => {
        let baseTotal = 0;
        let taxableTotal = 0;
        lineItems.forEach(item => {
            const amount = parseFloat(item.amount) || 0;
            baseTotal += amount;
            if (item.isGstApplicable) taxableTotal += amount;
        });
        const cgstAmount = (taxableTotal * (parseFloat(formData.cgstPercentage) || 0)) / 100;
        const sgstAmount = (taxableTotal * (parseFloat(formData.sgstPercentage) || 0)) / 100;
        const grossTotal = baseTotal + cgstAmount + sgstAmount;
        return { baseTotal, cgstAmount, sgstAmount, grossTotal };
    };

    const { baseTotal, cgstAmount, sgstAmount, grossTotal } = calculateTotals();

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setQrFile(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        const validItems = lineItems
            .map(i => ({
                ...i,
                finalDescription: i.description === 'Other' ? i.customDescription.trim() : i.description.trim()
            }))
            .filter(i => i.finalDescription !== '' && parseFloat(i.amount) > 0);

        if (validItems.length === 0) {
            toast.error("Please select a category and enter a valid amount");
            return;
        }

        if (!formData.dueDate) {
            toast.error("Please fill the Due Date");
            return;
        }

        if (!formData.selectedUnits || formData.selectedUnits.length === 0) {
            toast.error("Please select at least one unit");
            return;
        }

        setIsLoading(true);

        let qrCodeUrl = "";
        if (qrFile) {
            try {
                qrCodeUrl = await uploadImageToAzure(qrFile);
            } catch (err) {
                toast.error("Failed to upload QR code image");
                setIsLoading(false);
                return;
            }
        }

        const payload = {
            siteId: siteAdminData?.siteId,
            requestType: "SPLIT_EQUALITY",
            category: "Detailed Invoice",
            billingMonth: new Date().toISOString().split('T')[0],
            dueDate: formData.dueDate,
            penaltyAmount: formData.penaltyAmount || 0,
            cgstPercentage: formData.cgstPercentage || 0,
            sgstPercentage: formData.sgstPercentage || 0,
            qrCodeUrl: qrCodeUrl,
            units: formData.selectedUnits.map(u => ({ unitId: u.value, unitAdminId: u.unitAdminId || 1 })),
            lineItems: validItems.map(i => ({
                description: i.finalDescription,
                sacCode: i.sacCode,
                amount: i.amount,
                isGstApplicable: i.isGstApplicable
            }))
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: `/maintenance/generate`,
                data: payload,
                useNodeApi: true

            });
            if (res?.success) {
                toast.success("Maintenance invoices generated successfully!");
                navigate('/zentcomm/maintenance');
            } else {
                toast.error(res?.message || "Failed to generate invoices");
            }
        } catch (err) {
            toast.error("An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col flex-1 mt-4">
            <div>
                <p className="text-xs text-gray-500 dark:text-textsecondary mb-4 font-medium bg-[#F8FAFC] dark:bg-actionitembg p-3 rounded-lg border border-gray-100 dark:border-bordercolor">
                    The base amount will be split equally among all the selected units.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                    <CustomMultiSelect
                        label={<>Select Units <span className="text-red-500">*</span></>}
                        options={units}
                        value={formData.selectedUnits}
                        onChange={(selected) => setFormData(prev => ({ ...prev, selectedUnits: selected }))}
                        placeholder="Select Units"
                    />
                </div>
            </div>

            <div className="border border-gray-100 dark:border-bordercolor rounded-2xl p-5 bg-sectionheaderbg flex flex-col flex-1 mb-4">
                <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-4 pb-2 border-b dark:border-bordercolor">Invoice Details</h3>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5">
                    <div>
                        <CustomSelect
                            label={<>Select Categories <span className="text-red-500">*</span></>}
                            options={maintenanceCategories}
                            value={selectedCategories}
                            onChange={handleCategorySelect}
                            isMulti={true}
                            placeholder="Choose multiple categories..."
                        />
                    </div>

                    {lineItems.length > 0 && (
                        <div className="flex flex-col gap-3 mt-2">
                            <div className="flex gap-2 text-xs font-bold text-gray-500 dark:text-textsecondary px-2 pb-2 border-b border-gray-100 dark:border-bordercolor hidden md:flex">
                                <div className="w-1/3">Category</div>
                                <div className="w-24">SAC Code</div>
                                <div className="w-24 text-center">GST Apply?</div>
                                <div className="flex-1 text-right">Amount (₹)</div>
                            </div>

                            {lineItems.map((item) => (
                                <div key={item.id} className="flex flex-col md:flex-row gap-2 items-center bg-gray-50 md:bg-transparent p-3 md:p-0 rounded-lg md:rounded-none">
                                    <div className="w-full md:w-1/3 flex flex-col gap-2">
                                        <div className="font-bold text-xs text-gray-700 dark:text-textprimary py-2">{item.description}</div>
                                        {item.description === 'Other' && (
                                            <input
                                                type="text"
                                                placeholder="Enter custom category name"
                                                className="w-full h-10 px-4 text-xs border border-blue-200 dark:border-bordercolor bg-blue-50 dark:bg-actionitembg rounded-md outline-none focus:border-blue-500 text-textprimary"
                                                value={item.customDescription}
                                                onChange={(e) => handleLineItemChange(item.id, 'customDescription', e.target.value)}
                                            />
                                        )}
                                    </div>
                                    
                                    <input
                                        type="text"
                                        placeholder="SAC Code"
                                        className="w-full md:w-24 h-10 px-4 text-xs border border-gray-200 dark:border-bordercolor rounded-md outline-none bg-transparent text-textprimary focus:border-primary hover:border-gray-400"
                                        value={item.sacCode}
                                        onChange={(e) => handleLineItemChange(item.id, 'sacCode', e.target.value)}
                                    />
                                    
                                    <div className="w-full md:w-24 flex items-center justify-between md:justify-center p-2 md:p-0">
                                        <span className="md:hidden text-xs text-gray-500 dark:text-textsecondary">Apply GST?</span>
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                            checked={item.isGstApplicable}
                                            onChange={(e) => handleLineItemChange(item.id, 'isGstApplicable', e.target.checked)}
                                        />
                                    </div>
                                    
                                    <input
                                        type="number"
                                        placeholder="Amount"
                                        className="w-full md:flex-1 h-10 px-4 text-xs border border-gray-200 dark:border-bordercolor rounded-md outline-none bg-transparent text-textprimary focus:border-primary hover:border-gray-400 md:text-right font-semibold"
                                        value={item.amount}
                                        onChange={(e) => handleLineItemChange(item.id, 'amount', e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="bg-[#F8FAFC] dark:bg-actionitembg p-4 border border-gray-200 dark:border-bordercolor rounded-lg flex justify-end mt-2">
                        <div className="w-full md:w-64 flex flex-col gap-2 text-sm">
                            <div className="flex justify-between text-gray-600 dark:text-textsecondary">
                                <span>Sub Total:</span>
                                <span className="font-semibold text-tabletext">₹ {baseTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-gray-600 dark:text-textsecondary">
                                <div className="flex items-center gap-2">
                                    <span>CGST</span>
                                    <input 
                                        type="number" 
                                        id="cgstPercentage"
                                        className="w-12 h-8 px-2 text-xs border border-gray-200 dark:border-bordercolor rounded-md text-center bg-transparent text-textprimary"
                                        value={formData.cgstPercentage}
                                        onChange={handleInputChange}
                                    />
                                    <span>% :</span>
                                </div>
                                <span className="font-semibold text-tabletext">₹ {cgstAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-gray-600 dark:text-textsecondary">
                                <div className="flex items-center gap-2">
                                    <span>SGST</span>
                                    <input 
                                        type="number" 
                                        id="sgstPercentage"
                                        className="w-12 h-8 px-2 text-xs border border-gray-200 dark:border-bordercolor rounded-md text-center bg-transparent text-textprimary"
                                        value={formData.sgstPercentage}
                                        onChange={handleInputChange}
                                    />
                                    <span>% :</span>
                                </div>
                                <span className="font-semibold text-tabletext">₹ {sgstAmount.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-gray-300 dark:border-bordercolor my-1"></div>
                            <div className="flex justify-between text-gray-900 dark:text-textprimary font-bold text-base">
                                <span>Gross Total:</span>
                                <span className="text-blue-700">₹ {grossTotal.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="penaltyAmount">
                                Penalty Amount (₹)
                            </label>
                            <input type="number" id="penaltyAmount" value={formData.penaltyAmount} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" className="w-full h-10 px-4 border border-gray-200 dark:border-bordercolor rounded-md text-sm focus:outline-none focus:ring-1 focus:border-primary bg-transparent text-textprimary placeholder:text-gray-400 hover:border-gray-400 transition-all duration-200" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="dueDate">
                                Due Date <span className="text-red-500">*</span>
                            </label>
                            <input type="date" id="dueDate" value={formData.dueDate} onChange={handleInputChange} className="w-full h-10 px-4 border border-gray-200 dark:border-bordercolor rounded-md text-sm focus:outline-none focus:ring-1 focus:border-primary bg-transparent text-textprimary hover:border-gray-400 transition-all duration-200" />
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                            QR Code Image (Optional)
                        </label>
                        <div className="flex flex-col sm:flex-row gap-4 items-start">
                            <input
                                type="file"
                                id="qrCodeInput"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="w-full sm:w-2/3 h-10 px-4 py-1.5 border border-gray-200 dark:border-bordercolor rounded-md text-sm focus:outline-none focus:ring-1 focus:border-primary bg-transparent text-textprimary hover:border-gray-400 transition-all duration-200"
                            />
                            {qrFile && (
                                <div className="shrink-0 relative group p-1 border border-gray-200 dark:border-bordercolor rounded-lg bg-gray-50 dark:bg-actionitembg shadow-sm">
                                    <img
                                        src={URL.createObjectURL(qrFile)}
                                        alt="QR Preview"
                                        className="w-16 h-16 object-contain rounded"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setQrFile(null);
                                            document.getElementById('qrCodeInput').value = '';
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                                        title="Remove Image"
                                    >
                                        &times;
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                <button
                    type="button"
                    onClick={() => navigate('/zentcomm/maintenance')}
                    className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                        e.currentTarget.style.color = "var(--color-primaryaccent)";
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Generate'}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </form>
    );
};

export default SplitEqualityForm;
